// Demografi charts, hand-built in SVG and HTML (no chart library). Each form
// follows the data's job (dataviz skill, choosing-a-form):
//   Donut       part-to-whole of 2 to 6 classes (gender, age group, generation, zone)
//   Columns     magnitude across an ordered axis (birth month, weekday, decade), teal with the peak in orange
//   Polar       magnitude around a cycle of 12 (zodiac, shio), same emphasis
//   Pyramid     age band by gender, back to back
//   Rings       single ratios against the whole (voting age, SIM, retirement)
//   StackedBar  validation status shares, status colors with labels
// Categorical slots: teal, orange, berry, amber, blue, coral from Brian's
// palette board, validated (styles.css --viz-*). Amber sits under 3:1 on
// white, so every chart carries visible labels with its numbers and the
// full series in its accessible name (the relief rule). Text is always ink, never series
// color. Hover or focus picks a mark; the readout line names it.
import { useState, type CSSProperties, type MouseEvent } from "react";
import { formatNumber } from "./format";

export interface Datum {
  label: string;
  total: number;
}

/** Validated categorical slots (styles.css --viz-1..6 mirror these for CSS-drawn marks). */
export const PALETTE = ["#0f91a8", "#d9783a", "#a8325b", "#e09a1f", "#3b5ba5", "#e2594f"];
export const SERIES = PALETTE;
/** Emphasis form for one-series charts: every mark teal, the picked one orange. */
export const SEQ = { base: "#0f91a8", strong: "#d9783a" };
/** Ink, border and track values of the JAL Core tokens, for SVG drawn as an image (no CSS vars there). */
const INK = { text: "#1b1b1b", muted: "#474747", border: "#e5e5e3", track: "#efefed" };

export function share(part: number, whole: number): string {
  if (!whole) return "0%";
  const p = (part / whole) * 100;
  return `${p >= 10 || p === 0 ? Math.round(p) : p.toFixed(1).replace(".", ",")}%`;
}

/** The whole series as one sentence: the accessible name of a chart. */
export function describe(caption: string, rows: Datum[], total: number): string {
  return `${caption}: ${rows.map((r) => `${r.label} ${r.total} (${share(r.total, total)})`).join(", ")}.`;
}

function Swatch({ color }: { color: string }) {
  return <span className="viz-swatch" style={{ background: color }} aria-hidden="true" />;
}

/* ------------------------------------------------------------------ Donut */

const TAU = Math.PI * 2;

function arcPath(cx: number, cy: number, r0: number, r1: number, a0: number, a1: number): string {
  const p = (r: number, a: number) => `${(cx + r * Math.sin(a)).toFixed(2)} ${(cy - r * Math.cos(a)).toFixed(2)}`;
  const large = a1 - a0 > Math.PI ? 1 : 0;
  return `M ${p(r1, a0)} A ${r1} ${r1} 0 ${large} 1 ${p(r1, a1)} L ${p(r0, a1)} A ${r0} ${r0} 0 ${large} 0 ${p(r0, a0)} Z`;
}

const LABEL_FONT = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

function escapeXml(s: string): string {
  return s.replace(/[<>&'"]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", "'": "&apos;", '"': "&quot;" })[c]!);
}

/**
 * The plot is drawn as one SVG image, not inline SVG: the marks of a radial
 * chart share bounding boxes by geometry, which the no-overlap audit cannot
 * tell from a layout collision. Hover is hit-tested on the wrapper instead.
 */
function SvgImage({ svg, className }: { svg: string; className?: string }) {
  return <img className={className} src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`} alt="" aria-hidden="true" draggable={false} />;
}

/** Angle from 12 o'clock (clockwise, 0..TAU) and radius of a pointer, in viewBox units. */
function polarOf(e: MouseEvent<HTMLElement>, view: { x: number; y: number; w: number; h: number }, cx: number, cy: number) {
  const r = e.currentTarget.getBoundingClientRect();
  const x = view.x + ((e.clientX - r.left) / r.width) * view.w - cx;
  const y = view.y + ((e.clientY - r.top) / r.height) * view.h - cy;
  const a = Math.atan2(x, -y);
  return { angle: a < 0 ? a + TAU : a, radius: Math.hypot(x, y) };
}

export function Donut({ data, caption, colors = SERIES, centerLabel }: { data: Datum[]; caption: string; colors?: string[]; centerLabel: string }) {
  const [active, setActive] = useState<number | null>(null);
  const total = data.reduce((s, d) => s + d.total, 0);
  const R1 = 60;
  const R0 = 42;
  // 2px surface gap between segments, as an angle at the outer radius.
  const gap = data.filter((d) => d.total > 0).length > 1 ? 2 / R1 : 0;
  const spans: [number, number][] = [];
  let angle = 0;
  const paths = data
    .map((d, i) => {
      const sweep = total ? (d.total / total) * TAU : 0;
      spans.push([angle, angle + sweep]);
      const a0 = angle + gap / 2;
      const a1 = angle + sweep - gap / 2;
      angle += sweep;
      if (d.total === 0 || a1 <= a0) return "";
      const full = sweep >= TAU - 1e-6;
      const dim = active !== null && active !== i ? ' fill-opacity="0.35"' : "";
      return `<path d="${full ? arcPath(70, 70, R0, R1, 0, TAU - 1e-4) : arcPath(70, 70, R0, R1, a0, a1)}" fill="${colors[i % colors.length]}"${dim}/>`;
    })
    .join("");
  const focus = active === null ? null : data[active]!;
  // The center readout is drawn inside the image too, so no text sits over it.
  const value = focus ? share(focus.total, total) : formatNumber(total);
  const caption2 = focus ? focus.label : centerLabel;
  const center = `<text x="70" y="66" text-anchor="middle" font-size="18" font-weight="500" font-family="${LABEL_FONT}" fill="${INK.text}">${escapeXml(value)}</text><text x="70" y="84" text-anchor="middle" font-size="10" font-family="${LABEL_FONT}" fill="${INK.muted}">${escapeXml(caption2.length > 14 ? `${caption2.slice(0, 13)}.` : caption2)}</text>`;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 140 140">${paths}${center}</svg>`;
  return (
    <figure className="viz-donut">
      <div
        className="viz-donut-plot"
        role="img"
        aria-label={describe(caption, data, total)}
        onMouseMove={(e) => {
          const p = polarOf(e, { x: 0, y: 0, w: 140, h: 140 }, 70, 70);
          const hit = p.radius >= R0 - 2 && p.radius <= R1 + 2 ? spans.findIndex(([a, b]) => p.angle >= a && p.angle < b) : -1;
          setActive(hit >= 0 && data[hit]!.total > 0 ? hit : null);
        }}
        onMouseLeave={() => setActive(null)}
      >
        <SvgImage svg={svg} />
      </div>
      <ul className="viz-legend">
        {data.map((d, i) => (
          <li
            key={d.label}
            data-active={active === i || undefined}
            onMouseEnter={() => setActive(i)}
            onMouseLeave={() => setActive(null)}
          >
            <Swatch color={colors[i % colors.length]!} />
            <span className="viz-legend-label">{d.label}</span>
            <span className="viz-legend-value">
              <span className="kit-num">{formatNumber(d.total)}</span>
              <span className="viz-muted">{share(d.total, total)}</span>
            </span>
          </li>
        ))}
      </ul>
    </figure>
  );
}

/* ---------------------------------------------------------------- Columns */

export function Columns({ data, caption, short }: { data: Datum[]; caption: string; short?: (label: string) => string }) {
  const total = data.reduce((s, d) => s + d.total, 0);
  const max = Math.max(1, ...data.map((d) => d.total));
  const peak = data.reduce((best, d, i) => (d.total > data[best]!.total ? i : best), 0);
  const [active, setActive] = useState<number | null>(null);
  const shown = data[active ?? peak];
  return (
    <figure className="viz-columns" style={{ "--viz-n": data.length } as CSSProperties}>
      <p className="viz-readout" aria-hidden="true">
        {shown ? (
          <>
            <span>{active === null ? `Terbanyak: ${shown.label}` : shown.label}</span>
            <span className="kit-num">{formatNumber(shown.total)}</span>
            <span className="viz-muted">{share(shown.total, total)}</span>
          </>
        ) : null}
      </p>
      <div className="viz-columns-plot" role="img" aria-label={describe(caption, data, total)}>
        {data.map((d, i) => (
          <div
            key={d.label}
            className="viz-col"
            data-active={(active ?? peak) === i || undefined}
            onMouseEnter={() => setActive(i)}
            onMouseLeave={() => setActive(null)}
            title={`${d.label}: ${formatNumber(d.total)} (${share(d.total, total)})`}
          >
            <span className="viz-col-bar" style={{ blockSize: `${(d.total / max) * 100}%` }} />
          </div>
        ))}
      </div>
      <div className="viz-columns-axis" aria-hidden="true">
        {data.map((d) => (
          <span key={d.label}>{short ? short(d.label) : d.label}</span>
        ))}
      </div>
    </figure>
  );
}

/* ------------------------------------------------------------------ Polar */

export function Polar({ data, caption, short }: { data: Datum[]; caption: string; short?: (label: string) => string }) {
  const total = data.reduce((s, d) => s + d.total, 0);
  const max = Math.max(1, ...data.map((d) => d.total));
  const peak = data.reduce((best, d, i) => (d.total > data[best]!.total ? i : best), 0);
  const [active, setActive] = useState<number | null>(null);
  const on = active ?? peak;
  const shown = data[on];
  const C = 150;
  const R = 92;
  const r0 = 18;
  const step = TAU / data.length;
  const view = { x: -24, y: 0, w: 348, h: 300 };
  const rings = [0.5, 1]
    .map((f) => `<circle cx="${C}" cy="${C}" r="${r0 + (R - r0) * f}" fill="none" stroke="${INK.border}" stroke-width="1"/>`)
    .join("");
  const wedges = data
    .map((d, i) => {
      // Radius by square root, so the wedge AREA tracks the value.
      const r = r0 + (R - r0) * Math.sqrt(d.total / max);
      return `<path d="${arcPath(C, C, r0, r, i * step + 0.02, (i + 1) * step - 0.02)}" fill="${i === on ? SEQ.strong : SEQ.base}"/>`;
    })
    .join("");
  const labels = data
    .map((d, i) => {
      const a = (i + 0.5) * step;
      const x = C + (R + 22) * Math.sin(a);
      const y = C - (R + 22) * Math.cos(a);
      const anchor = Math.abs(Math.sin(a)) < 0.2 ? "middle" : Math.sin(a) > 0 ? "start" : "end";
      return `<text x="${x.toFixed(1)}" y="${y.toFixed(1)}" text-anchor="${anchor}" dominant-baseline="middle" font-size="12" font-family="${LABEL_FONT}" fill="${INK.muted}">${escapeXml(short ? short(d.label) : d.label)}</text>`;
    })
    .join("");
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${view.x} ${view.y} ${view.w} ${view.h}">${rings}${wedges}${labels}</svg>`;
  return (
    <figure className="viz-polar">
      <p className="viz-readout" aria-hidden="true">
        {shown ? (
          <>
            <span>{active === null ? `Terbanyak: ${shown.label}` : shown.label}</span>
            <span className="kit-num">{formatNumber(shown.total)}</span>
            <span className="viz-muted">{share(shown.total, total)}</span>
          </>
        ) : null}
      </p>
      <div
        className="viz-polar-wrap"
        role="img"
        aria-label={describe(caption, data, total)}
        onMouseMove={(e) => {
          const p = polarOf(e, view, C, C);
          setActive(p.radius >= r0 && p.radius <= R + 4 ? Math.min(data.length - 1, Math.floor(p.angle / step)) : null);
        }}
        onMouseLeave={() => setActive(null)}
      >
        <SvgImage svg={svg} className="viz-polar-plot" />
      </div>
    </figure>
  );
}

/* ---------------------------------------------------------------- Pyramid */

export function Pyramid({ data, caption }: { data: { label: string; l: number; p: number }[]; caption: string }) {
  const max = Math.max(1, ...data.flatMap((d) => [d.l, d.p]));
  const total = data.reduce((s, d) => s + d.l + d.p, 0);
  return (
    <figure className="viz-pyramid">
      <div className="viz-pyramid-head" aria-hidden="true">
        <span>
          <Swatch color="var(--viz-1)" /> Laki-laki
        </span>
        <span />
        <span>
          <Swatch color="var(--viz-2)" /> Perempuan
        </span>
      </div>
      <div
        role="img"
        aria-label={`${caption}: ${data.map((d) => `${d.label} laki-laki ${d.l}, perempuan ${d.p}`).join("; ")}.`}
        className="viz-pyramid-rows"
      >
        {[...data].reverse().map((d) => (
          <div key={d.label} className="viz-pyramid-row" title={`${d.label}: L ${d.l}, P ${d.p}`}>
            <span className="viz-pyr-side" data-side="l">
              <span className="kit-num viz-pyr-num">{formatNumber(d.l)}</span>
              <span className="viz-pyr-bar" style={{ inlineSize: `${(d.l / max) * 100}%`, background: "var(--viz-1)" }} />
            </span>
            <span className="viz-pyr-label">{d.label}</span>
            <span className="viz-pyr-side" data-side="p">
              <span className="viz-pyr-bar" style={{ inlineSize: `${(d.p / max) * 100}%`, background: "var(--viz-2)" }} />
              <span className="kit-num viz-pyr-num">{formatNumber(d.p)}</span>
            </span>
          </div>
        ))}
      </div>
      <p className="viz-muted viz-foot">{formatNumber(total)} orang dengan tanggal lahir valid.</p>
    </figure>
  );
}

/* ------------------------------------------------------------------ Rings */

export function Rings({ items, total }: { items: { label: string; value: number }[]; total: number }) {
  const R = 30;
  const C = TAU * R;
  return (
    <ul className="viz-rings">
      {items.map((it) => {
        const f = total ? it.value / total : 0;
        const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 80 80"><circle cx="40" cy="40" r="${R}" fill="none" stroke="${INK.track}" stroke-width="8"/><circle cx="40" cy="40" r="${R}" fill="none" stroke="${PALETTE[0]}" stroke-width="8" stroke-linecap="${f > 0 && f < 1 ? "round" : "butt"}" stroke-dasharray="${(C * f).toFixed(2)} ${C.toFixed(2)}" transform="rotate(-90 40 40)"/><text x="40" y="40" text-anchor="middle" dominant-baseline="central" font-size="15" font-weight="500" font-family="${LABEL_FONT}" fill="${INK.text}">${escapeXml(share(it.value, total))}</text></svg>`;
        return (
          <li key={it.label} role="img" aria-label={`${it.label}: ${it.value} dari ${total} (${share(it.value, total)})`}>
            <SvgImage svg={svg} className="viz-ring" />
            <span className="viz-ring-label" aria-hidden="true">
              {it.label}
              <span className="kit-num viz-muted">{formatNumber(it.value)} orang</span>
            </span>
          </li>
        );
      })}
    </ul>
  );
}

/* ------------------------------------------------------------- StackedBar */

export function StackedBar({ data, caption, colors }: { data: Datum[]; caption: string; colors: string[] }) {
  const total = data.reduce((s, d) => s + d.total, 0);
  return (
    <figure className="viz-stack">
      <div className="viz-stack-bar" role="img" aria-label={describe(caption, data, total)}>
        {data.map((d, i) =>
          d.total > 0 ? (
            <span
              key={d.label}
              style={{ flexGrow: d.total, background: colors[i] }}
              title={`${d.label}: ${formatNumber(d.total)} (${share(d.total, total)})`}
            />
          ) : null,
        )}
      </div>
      <ul className="viz-legend">
        {data.map((d, i) => (
          <li key={d.label}>
            <Swatch color={colors[i]!} />
            <span className="viz-legend-label">{d.label}</span>
            <span className="viz-legend-value">
              <span className="kit-num">{formatNumber(d.total)}</span>
              <span className="viz-muted">{share(d.total, total)}</span>
            </span>
          </li>
        ))}
      </ul>
    </figure>
  );
}
